import { useState } from 'react';
import type { ComplimentaryCareEnrollment } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';

/** Matches ComplimentaryCareHrNote.BodyMaxLength on the API, so the box caps instead of failing on save. */
const maxLength = 4000;

interface HrNoteCardProps {
  enrollment: ComplimentaryCareEnrollment;

  /** Handed the enrollment the save returned — who saved it and when come back with it. */
  onSaved: (enrollment: ComplimentaryCareEnrollment) => void;
}

/**
 * HR's own notes about the employee — enrollment questions they are chasing, what was checked against
 * an original, who they spoke to. Saved on its own, deliberately: a note is not a decision, and HR
 * writes these while an enrollment is sitting in the queue as often as when they are approving it.
 *
 * Two things the wording on the page has to carry, because neither is guessable from a text box:
 * the employee never sees this, and it stays with the person rather than with the form — an approved
 * change replaces the enrollment row underneath it and the note is still here afterwards.
 */
export function HrNoteCard({ enrollment, onSaved }: HrNoteCardProps) {
  const { api } = useHubContext();

  // Seeded once per mounted enrollment, and `saved` is what "unsaved changes" is measured against.
  // The detail page re-reads the enrollment after every decision, so comparing against the prop
  // directly would call the box clean the moment anything else on the page changed.
  const [draft, setDraft] = useState(enrollment.hrNote ?? '');
  const [saved, setSaved] = useState(enrollment.hrNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = draft !== saved;

  async function save() {
    setBusy(true);
    setError(null);

    try {
      const updated = await api.saveComplimentaryCareHrNote(enrollment.id, draft);
      setSaved(updated.hrNote ?? '');
      setDraft(updated.hrNote ?? '');
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The note could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="group">
      <h2 className="group__legend">HR notes</h2>

      <p className="care-note__hint">
        Internal to HR — the employee never sees this. Kept against the employee, not this form.
      </p>

      {error && (
        <div className="issues__block issues__block--error">
          <p>{error}</p>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="hrNote">
          Notes
        </label>
        <textarea
          id="hrNote"
          className="control"
          rows={5}
          value={draft}
          maxLength={maxLength}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>

      <div className="care-note__footer">
        <span className="care-note__meta">
          {dirty ? (
            <strong>Unsaved changes</strong>
          ) : enrollment.hrNoteUpdatedByUpn ? (
            <>
              Last edited by <strong>{enrollment.hrNoteUpdatedByUpn}</strong>
              {enrollment.hrNoteUpdatedAtUtc &&
                ` on ${new Date(enrollment.hrNoteUpdatedAtUtc).toLocaleString()}`}
            </>
          ) : (
            'No notes yet.'
          )}
        </span>

        <button
          type="button"
          className="button button--primary button--small"
          disabled={busy || !dirty}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save notes'}
        </button>
      </div>
    </section>
  );
}
